/**
 * The message contract between PiezoWorkerPool and the sign worker.
 *
 * Both sides used to declare their own half: the pool typed what it received, the worker typed what it
 * received, `WorkerErrorCode` was written out twice, and the pool's postMessage calls were untyped object
 * literals. So a renamed field on one side compiled fine and failed at run time, in a thread whose only
 * report is a console line. Declaring the contract once, and having both sides import it, is what makes a
 * mismatch a build error instead.
 */

import type { PiezoGetPiezoExecutionContextInput } from '../provisioning/PiezoClient.types';

/** Why a sign failed, as reported by the worker. */
export type WorkerErrorCode = 'MIDWAY_AUTH_REQUIRED' | 'SIGN_FAILED' | 'NOT_INITIALIZED';

/** Pool -> worker. */
export type PoolToWorker =
  | {
      type: 'INIT';
      region: string;
      /** The pool's slot label, echoed into the worker's logs so concurrent workers can be told apart. */
      label?: string;
      /** The main thread's already-compiled binary. Absent means the worker compiles its own. */
      wasmModule?: WebAssembly.Module;
    }
  | { id: number; type: 'SIGN'; input: PiezoGetPiezoExecutionContextInput };

/** Worker -> pool. */
export type WorkerToPool =
  | { type: 'READY' }
  | { type: 'INIT_ERROR'; message: string }
  | { id: number; type: 'RESULT'; artifactBundle: string }
  | { id: number; type: 'ERROR'; code: WorkerErrorCode; message?: string };

/**
 * Signals an expired Midway session — the typed form of the MIDWAY_AUTH_REQUIRED code above, which is why
 * it lives with the protocol rather than with the pool. Callers treat it differently from a generic sign
 * failure: no retry anywhere, because every Piezo call is Midway-authenticated and another worker fails the
 * same way. Only the operator reloading can restore the session.
 */
export class MidwayAuthRequiredError extends Error {
  constructor(message = 'Midway auth required') {
    super(message);
    this.name = 'MidwayAuthRequiredError';
    Object.setPrototypeOf(this, MidwayAuthRequiredError.prototype);
  }
}
